/**
 * 原始鼠标事件管理器。
 * 仅负责监听容器鼠标事件并向外派发，不承载业务逻辑。
 */
import {Subject, take} from 'rxjs';
import type {Observable, Subscription} from 'rxjs';
import type {Viewer} from '../Viewer';


/**
 * 原始鼠标事件类型枚举。
 */
export enum RawPointerEventType {
    /** 鼠标按下事件。 */
    MouseDown = 'mousedown',
    /** 鼠标抬起事件。 */
    MouseUp = 'mouseup',
    /** 鼠标交互被视口边界或窗口状态取消。 */
    PointerCancel = 'pointercancel',
    /** 鼠标移动事件。 */
    MouseMove = 'mousemove',
    /** 鼠标单击事件。 */
    Click = 'click',
    /** 鼠标双击事件。 */
    DoubleClick = 'dblclick',
}

/**
 * 原始鼠标事件派发载荷。
 */
export interface RawPointerEventPayload {
    /** 事件类型。 */
    type: RawPointerEventType;
    /** 浏览器原始事件对象。 */
    nativeEvent: MouseEvent;
    /** 事件触发时的客户端 X 坐标。 */
    clientX: number;
    /** 事件触发时的客户端 Y 坐标。 */
    clientY: number;
    /** 事件触发时鼠标按键编号。 */
    button: number;
    /** 事件触发时鼠标按键位掩码。 */
    buttons: number;
    /** Ctrl 键是否按下。 */
    ctrlKey: boolean;
    /** Shift 键是否按下。 */
    shiftKey: boolean;
    /** Alt 键是否按下。 */
    altKey: boolean;
    /** Meta 键是否按下。 */
    metaKey: boolean;
    /** 事件时间戳。 */
    timestamp: number;
}


/**
 * 原始鼠标事件管理器。
 */
export class EventManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** Ctrl 键是否处于长按状态。 */
    public isCtrlPressed: boolean = false;
    /** Shift 键是否处于长按状态。 */
    public isShiftPressed: boolean = false;
    /** 事件监听容器。 */
    private readonly container: HTMLElement;
    /** 各事件类型对应的事件流。 */
    private readonly eventStreams: Record<RawPointerEventType, Subject<RawPointerEventPayload>>;
    /** 指针已离开或失去有效上下文；独立于拖拽取消，不伪造鼠标业务事件。 */
    private readonly pointerContextInvalidated = new Subject<void>();
    /** 修饰键状态变更通知；只在状态改变时发布，供静止指针下的交互显示订阅。 */
    private readonly modifierKeysChanged = new Subject<void>();
    /** 是否已完成 DOM 事件绑定。 */
    private isBound: boolean = false;

    /** 当前由 canvas 发起的鼠标会话按钮；没有会话时为 null。 */
    private activeMouseButton: number | null = null;

    /** 当前鼠标会话按下时的 buttons 掩码，用于合成取消事件。 */
    private activeMouseButtons: number = 0;
    /** 最后一次位于 Viewer 容器内的鼠标客户端 X 坐标。 */
    private lastPointerClientX: number | null = null;
    /** 最后一次位于 Viewer 容器内的鼠标客户端 Y 坐标。 */
    private lastPointerClientY: number | null = null;


    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     */
    constructor(viewer: Viewer) {
        const container = viewer.container;
        if (!container) {
            throw new Error('Viewer 容器未初始化，无法创建原始鼠标事件管理器');
        }
        this.viewer = viewer;
        this.container = container;
        this.eventStreams = {
            [RawPointerEventType.MouseDown]: new Subject<RawPointerEventPayload>(),
            [RawPointerEventType.MouseUp]: new Subject<RawPointerEventPayload>(),
            [RawPointerEventType.PointerCancel]: new Subject<RawPointerEventPayload>(),
            [RawPointerEventType.MouseMove]: new Subject<RawPointerEventPayload>(),
            [RawPointerEventType.Click]: new Subject<RawPointerEventPayload>(),
            [RawPointerEventType.DoubleClick]: new Subject<RawPointerEventPayload>(),
        };
    }

    /**
     * 绑定 DOM 鼠标事件。
     */
    bind(): void {
        // 已绑定时直接返回，避免重复注册。
        if (this.isBound) {
            return;
        }

        // 统一在同一容器上绑定，保证事件来源一致。
        this.container.addEventListener('mousedown', this.handleMouseDown);
        this.container.addEventListener('mouseup', this.handleMouseUp);
        this.container.addEventListener('mousemove', this.handleMouseMove);
        this.container.addEventListener('mouseleave', this.handleContainerMouseLeave);
        this.container.addEventListener('pointercancel', this.handleNativePointerCancel);
        this.container.addEventListener('click', this.handleClick);
        this.container.addEventListener('dblclick', this.handleDoubleClick);


        // 仅在当前容器上监听键盘与失焦事件，保证事件域与交互容器一致。
        this.container.addEventListener('keydown', this.handleKeyDown);
        this.container.addEventListener('keyup', this.handleKeyUp);
        this.container.addEventListener('blur', this.handleWindowBlur);
        // 未按键悬停也会因窗口失焦失效，不能只在拖拽会话中订阅。
        window.addEventListener('blur', this.handleWindowBlur);


        this.isBound = true;
    }

    /**
     * 解绑 DOM 鼠标事件。
     */
    unbind(): void {
        // 解绑意味着画布不再拥有有效鼠标上下文，必须同步清空粘贴落点。
        this.clearLastPointerClientPosition();
        this.pointerContextInvalidated.next();
        // 未绑定时无需重复解绑。
        if (!this.isBound) {
            return;
        }

        // 与 bind 完全对称移除，避免泄漏。
        this.container.removeEventListener('mousedown', this.handleMouseDown);
        this.container.removeEventListener('mouseup', this.handleMouseUp);
        this.container.removeEventListener('mousemove', this.handleMouseMove);
        this.container.removeEventListener('mouseleave', this.handleContainerMouseLeave);
        this.container.removeEventListener('pointercancel', this.handleNativePointerCancel);
        this.container.removeEventListener('click', this.handleClick);
        this.container.removeEventListener('dblclick', this.handleDoubleClick);


        // 与绑定逻辑保持对称，避免容器事件监听泄漏。
        this.container.removeEventListener('keydown', this.handleKeyDown);
        this.container.removeEventListener('keyup', this.handleKeyUp);
        this.container.removeEventListener('blur', this.handleWindowBlur);
        window.removeEventListener('blur', this.handleWindowBlur);
        this.endMouseSession();
        this.updateModifierKeyState(false, false);


        this.isBound = false;
    }

    /**
     * 获取指定原始事件类型对应的可观察事件流。
     * @param type 事件类型。
     * @returns 对应事件流。
     */
    observe(type: RawPointerEventType): Observable<RawPointerEventPayload> {
        return this.eventStreams[type].asObservable();
    }

    /** @returns 无按键离开、失焦或原生取消的上下文失效通知，不改变原始事件流。 */
    public observePointerContextInvalidated(): Observable<void> {
        return this.pointerContextInvalidated.asObservable();
    }

    /**
     * 订阅 Ctrl/Shift 状态变化，收到通知后读取本管理器的当前键状态。
     *
     * 键盘、鼠标载荷、失焦和解绑共用同一写入入口，消费者无需重复监听 DOM 键盘事件。
     * @returns 不重复发布相同状态的变更流。
     */
    public observeModifierKeysChanged(): Observable<void> {
        return this.modifierKeysChanged.asObservable();
    }

    /**
     * 监听一次事件。
     * @param type 事件类型。
     * @param listener 监听回调。
     * @returns 订阅对象。
     */
    once(type: RawPointerEventType, listener: (payload: RawPointerEventPayload) => void): Subscription {
        return this.eventStreams[type].pipe(take(1)).subscribe(listener);
    }

    /**
     * 读取最后一次位于 Viewer 容器内的鼠标位置。
     *
     * @returns 客户端坐标副本；鼠标尚未进入或已经离开容器时返回 null。
     */
    public getLastPointerClientPosition(): {
        clientX: number;
        clientY: number;
    } | null {
        if (this.lastPointerClientX === null || this.lastPointerClientY === null) {
            return null;
        }
        return {
            clientX: this.lastPointerClientX,
            clientY: this.lastPointerClientY,
        };
    }

    /**
     * 释放资源。
     */
    dispose(): void {
        // 先解绑 DOM 事件，避免后续仍触发回调。
        this.unbind();
        this.pointerContextInvalidated.complete();
        this.modifierKeysChanged.complete();
        (Object.values(RawPointerEventType) as RawPointerEventType[]).forEach((eventType) => {
            this.eventStreams[eventType].complete();
        });
    }

    /** 鼠标按下事件处理器。 */
    private readonly handleMouseDown = (event: MouseEvent): void => {
        this.rememberPointerClientPosition(event);
        this.beginMouseSession(event);
        this.viewer.dispatchEvent({ type: 'pointerdown', event: event } as never)

        this.eventStreams[RawPointerEventType.MouseDown].next(this.createPayload(RawPointerEventType.MouseDown, event));
    };

    /** 鼠标抬起事件处理器。 */
    private readonly handleMouseUp = (event: MouseEvent): void => {
        this.rememberPointerClientPosition(event);
        this.eventStreams[RawPointerEventType.MouseUp].next(this.createPayload(RawPointerEventType.MouseUp, event));
        this.endMouseSession();
    };


    /** 鼠标移动事件处理器。 */
    private readonly handleMouseMove = (event: MouseEvent): void => {
        this.rememberPointerClientPosition(event);
        if (this.activeMouseButton !== null && event.buttons === 0) {
            this.dispatchPointerCancel(event);
            return;
        }
        this.eventStreams[RawPointerEventType.MouseMove].next(this.createPayload(RawPointerEventType.MouseMove, event));
    };

    /** 鼠标按住离开 canvas 时，立即结束当前图形区操作。 */
    private readonly handleContainerMouseLeave = (event: MouseEvent): void => {
        // 鼠标离开后不允许 Ctrl+V 继续使用过期画布坐标。
        this.clearLastPointerClientPosition();
        this.pointerContextInvalidated.next();
        if (this.activeMouseButton === null || event.buttons === 0) {
            return;
        }
        this.dispatchPointerCancel(event);
    };

    /** 鼠标释放发生在 canvas 外部时的兜底收口。 */
    private readonly handleWindowMouseUp = (event: MouseEvent): void => {
        if (this.activeMouseButton === null) {
            return;
        }
        this.eventStreams[RawPointerEventType.MouseUp].next(this.createPayload(
            RawPointerEventType.MouseUp,
            event,
            {
                button: this.activeMouseButton,
                buttons: 0,
            },
        ));
        this.endMouseSession();
    };

    /** 鼠标单击事件处理器。 */
    private readonly handleClick = (event: MouseEvent): void => {
        this.rememberPointerClientPosition(event);
        this.eventStreams[RawPointerEventType.Click].next(this.createPayload(RawPointerEventType.Click, event));
    };

    /** 鼠标双击事件处理器。 */
    private readonly handleDoubleClick = (event: MouseEvent): void => {
        this.rememberPointerClientPosition(event);
        this.eventStreams[RawPointerEventType.DoubleClick].next(this.createPayload(RawPointerEventType.DoubleClick, event));
    };


    /** 键盘按下事件处理器。 */
    private readonly handleKeyDown = (event: KeyboardEvent): void => {
        this.updateModifierKeyState(event.ctrlKey, event.shiftKey);
    };

    /** 键盘抬起事件处理器。 */
    private readonly handleKeyUp = (event: KeyboardEvent): void => {
        this.updateModifierKeyState(event.ctrlKey, event.shiftKey);
    };

    /** 窗口失焦事件处理器。 */
    private readonly handleWindowBlur = (): void => {
        this.clearLastPointerClientPosition();
        this.pointerContextInvalidated.next();
        if (this.activeMouseButton !== null) {
            this.dispatchPointerCancel(new MouseEvent('pointercancel', {
                bubbles: true,
                cancelable: true,
                button: this.activeMouseButton,
                buttons: this.activeMouseButtons,
            }));
        }
        this.updateModifierKeyState(false, false);
    };

    /** 原生 pointercancel 同时取消 Hover 上下文和既有鼠标会话。 */
    private readonly handleNativePointerCancel = (event: MouseEvent): void => {
        this.clearLastPointerClientPosition();
        this.pointerContextInvalidated.next();
        this.dispatchPointerCancel(event);
    };

    /**
     * 记录一次 Viewer 容器内鼠标位置。
     *
     * @param event 浏览器鼠标事件。
     */
    private rememberPointerClientPosition(event: MouseEvent): void {
        this.lastPointerClientX = event.clientX;
        this.lastPointerClientY = event.clientY;
    }

    /**
     * 清空最后一次 Viewer 容器内鼠标位置。
     */
    private clearLastPointerClientPosition(): void {
        this.lastPointerClientX = null;
        this.lastPointerClientY = null;
    }

    /**
     * 开始一次由 canvas 发起的鼠标会话。
     *
     * @param event 鼠标按下事件。
     */
    private beginMouseSession(event: MouseEvent): void {
        this.endMouseSession();
        this.activeMouseButton = event.button;
        this.activeMouseButtons = event.buttons || this.resolveButtonMask(event.button);
        window.addEventListener('mouseup', this.handleWindowMouseUp);
    }

    /**
     * 结束当前鼠标会话并移除窗口级兜底监听。
     */
    private endMouseSession(): void {
        window.removeEventListener('mouseup', this.handleWindowMouseUp);
        this.activeMouseButton = null;
        this.activeMouseButtons = 0;
    }

    /**
     * 派发统一的指针取消事件。
     *
     * @param event 触发取消的浏览器事件。
     */
    private dispatchPointerCancel(event: MouseEvent): void {
        if (this.activeMouseButton === null) {
            return;
        }
        this.eventStreams[RawPointerEventType.PointerCancel].next(this.createPayload(
            RawPointerEventType.PointerCancel,
            event,
            {
                button: this.activeMouseButton,
                buttons: this.activeMouseButtons,
            },
        ));
        this.endMouseSession();
    }

    /**
     * 将 MouseEvent.button 转换为 MouseEvent.buttons 掩码。
     *
     * @param button 鼠标按钮编号。
     * @returns buttons 掩码。
     */
    private resolveButtonMask(button: number): number {
        if (button === 1) {
            return 4;
        }
        if (button === 2) {
            return 2;
        }
        return 1;
    }

    /**
     * 构建原始鼠标事件载荷。
     * @param type 事件类型。
     * @param nativeEvent 浏览器原始事件对象。
     * @returns 标准化事件载荷。
     */
    private createPayload(
        type: RawPointerEventType,
        nativeEvent: MouseEvent,
        overrides: Partial<Pick<RawPointerEventPayload, 'button' | 'buttons'>> = {},
    ): RawPointerEventPayload {

        // 鼠标事件自带修饰键瞬时状态，顺带同步管理器内部长按态，避免丢失前序键盘事件时状态不一致。
        this.updateModifierKeyState(nativeEvent.ctrlKey, nativeEvent.shiftKey);


        return {
            type,
            nativeEvent,
            clientX: nativeEvent.clientX,
            clientY: nativeEvent.clientY,
            button: overrides.button ?? nativeEvent.button,
            buttons: overrides.buttons ?? nativeEvent.buttons,
            ctrlKey: nativeEvent.ctrlKey,
            shiftKey: nativeEvent.shiftKey,
            altKey: nativeEvent.altKey,
            metaKey: nativeEvent.metaKey,
            timestamp: Date.now(),
        };
    }

    /**
     * 同步修饰键长按状态。
     * @param isCtrlPressed 当前 Ctrl 键是否按下。
     * @param isShiftPressed 当前 Shift 键是否按下。
     */
    private updateModifierKeyState(isCtrlPressed: boolean, isShiftPressed: boolean): void {
        if (this.isCtrlPressed === isCtrlPressed && this.isShiftPressed === isShiftPressed) {
            return;
        }
        // 统一经由该方法写入，保证不同事件源下状态收敛一致。
        this.isCtrlPressed = isCtrlPressed;
        this.isShiftPressed = isShiftPressed;
        this.modifierKeysChanged.next();
    }
}
